import { useState } from 'react'

import { useQuery } from '@apollo/client'
import { Badge, Switch, Tooltip, ActionIcon, Menu } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import AvatarGroup from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { EmployeeStatus } from 'src/graphql/types/employees'
import { WebhookStatus, AuthType } from 'src/graphql/types/webhooks'
import {
  WEBHOOKS_QUERY,
  UPDATE_WEBHOOK,
  DELETE_WEBHOOK,
} from 'src/graphql/webhooks.graphql'
import IconCopy from 'src/icons/IconCopy'
import IconThreeDots from 'src/icons/IconThreeDots'
import { formatSnakeValue } from 'src/lib/formatters'

import WebhookDrawer from './components/WebhookDrawer'

const DEFAULT_LIMIT = 20

const WebhooksTab = () => {
  const [offset, setOffset] = useState(0)
  const [limit] = useState(DEFAULT_LIMIT)
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false)
  const [selectedWebhookId, setSelectedWebhookId] = useState<string | null>(
    null
  )
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)
  const [webhookToDelete, setWebhookToDelete] = useState<any>(null)

  const { data, loading } = useQuery(WEBHOOKS_QUERY, {
    variables: {
      pageInfo: { offset, limit },
    },
    fetchPolicy: 'cache-and-network',
  })

  const [updateWebhook] = useMutation(UPDATE_WEBHOOK, {
    onCompleted: () => {
      toast('Webhook updated successfully', 'success')
    },
    onError: (e) => toast(e?.message || 'Unable to update webhook', 'error'),
    refetchQueries: [WEBHOOKS_QUERY],
  })

  const [deleteWebhook] = useMutation(DELETE_WEBHOOK, {
    onCompleted: () => {
      toast('Webhook deleted successfully', 'success')
    },
    onError: (e) => toast(e?.message || 'Unable to delete webhook', 'error'),
    refetchQueries: [WEBHOOKS_QUERY],
  })

  const webhooks = data?.webhooks?.items || []
  const totalCount = data?.webhooks?.totalCount || 0
  const hasNextPage = data?.webhooks?.pageInfo?.hasNextPage || false
  const hasPreviousPage = data?.webhooks?.pageInfo?.hasPreviousPage || false

  const handleNextPage = () => {
    if (hasNextPage) {
      setOffset(offset + limit)
    }
  }

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setOffset(Math.max(0, offset - limit))
    }
  }

  const handleToggleStatus = (webhook) => {
    const newStatus =
      webhook.webhookStatus === WebhookStatus.ACTIVE
        ? WebhookStatus.INACTIVE
        : WebhookStatus.ACTIVE
    updateWebhook({
      variables: {
        webhookId: webhook.webhookId,
        input: { webhookStatus: newStatus },
      },
    })
  }

  const handleCopyId = (webhookId: string) => {
    navigator.clipboard.writeText(webhookId)
    toast('Webhook ID copied to clipboard', 'success')
  }

  const handleEditWebhook = (webhookId: string) => {
    setSelectedWebhookId(webhookId)
    openModal()
  }

  const handleDeleteWebhook = (webhook) => {
    setWebhookToDelete(webhook)
    openDeleteModal()
  }

  const confirmDeleteWebhook = async () => {
    if (webhookToDelete) {
      await deleteWebhook({
        variables: { webhookId: webhookToDelete.webhookId },
      })
    }
  }

  const handleCloseModal = () => {
    setSelectedWebhookId(null)
    closeModal()
  }

  const handleAddWebhook = () => {
    setSelectedWebhookId(null)
    openModal()
  }

  // Column definitions for DnTable
  const columns = [
    {
      key: 'webhookId',
      label: 'ID',
      width: 4,
      render: (webhook) => (
        <Tooltip
          label={webhook.webhookId}
          withArrow
          className="font-mono text-xs"
        >
          <ActionIcon
            key={`id-${webhook.webhookId}`}
            variant="subtle"
            color="gray"
            size={18}
            onClick={(e) => {
              e.stopPropagation()
              handleCopyId(webhook.webhookId)
            }}
          >
            <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
      ),
    },
    {
      key: 'url',
      label: 'URL',
      width: 38,
      render: (webhook) => (
        <div
          key={`url-${webhook.webhookId}`}
          className="truncate text-sm text-doubleNickel-gray-900"
          title={webhook.url}
        >
          {webhook.url}
        </div>
      ),
    },
    {
      key: 'authType',
      label: 'Auth Type',
      width: 12,
      render: (webhook) => (
        <div key={`auth-${webhook.webhookId}`}>
          <Badge
            variant="light"
            color={webhook.authType === AuthType.OAUTH2 ? 'blue' : 'gray'}
            size="sm"
          >
            {formatSnakeValue(webhook.authType)}
          </Badge>
        </div>
      ),
    },
    {
      key: 'createdBy',
      label: 'Created By',
      width: 13,
      render: (webhook) => (
        <div key={`createdBy-${webhook.webhookId}`}>
          <AvatarGroup
            avatars={[
              {
                firstName: webhook.createdBy?.firstName,
                lastName: webhook.createdBy?.lastName,
                isInactive: webhook.createdBy?.status !== EmployeeStatus.ACTIVE,
              },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created At',
      width: 18,
      render: (webhook) => (
        <div
          key={`created-${webhook.webhookId}`}
          className="text-sm text-doubleNickel-gray-600"
        >
          {dayjs(webhook.createdAt).format('MM/DD/YYYY h:mmA')}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 15,
      render: (webhook) => (
        <div
          key={`actions-${webhook.webhookId}`}
          className="flex items-center justify-end gap-2"
        >
          <Tooltip
            label={
              webhook.webhookStatus === WebhookStatus.ACTIVE
                ? 'Disable webhook - Stop sending events to this endpoint'
                : 'Enable webhook - Start sending events to this endpoint'
            }
            withArrow
          >
            <div onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={webhook.webhookStatus === WebhookStatus.ACTIVE}
                onChange={() => handleToggleStatus(webhook)}
                size="sm"
              />
            </div>
          </Tooltip>
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown className="w-40">
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteWebhook(webhook)
                }}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      ),
    },
  ]

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex-shrink-0">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className="text-lg font-semibold text-doubleNickel-gray-900">
              Webhooks
            </div>
            <div className="text-sm text-doubleNickel-gray-600">
              {loading
                ? 'loading...'
                : `${totalCount} webhook${totalCount === 1 ? '' : 's'}`}
            </div>
          </div>
          <DNButton variant="primary" onClick={handleAddWebhook}>
            Add Webhook
          </DNButton>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={webhooks}
          enableColumnResize={true}
          loading={loading}
          emptyStateMessage="No webhooks found"
          enablePagination={false}
          onRowClick={(_rowIndex, webhook) =>
            handleEditWebhook(webhook.webhookId)
          }
          rowClassName="cursor-pointer hover:bg-doubleNickel-gray-50"
        />
      </div>

      {totalCount > limit && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-doubleNickel-gray-600">
            Showing {offset + 1}-{Math.min(offset + limit, totalCount)} of{' '}
            {totalCount}
          </div>
          <div className="flex gap-2">
            <DNButton
              variant="secondary"
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || loading}
              size="sm"
            >
              Previous
            </DNButton>
            <DNButton
              variant="secondary"
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
              size="sm"
            >
              Next
            </DNButton>
          </div>
        </div>
      )}

      <WebhookDrawer
        opened={modalOpened}
        close={handleCloseModal}
        webhookId={selectedWebhookId}
      />

      <DeleteModal
        title="Delete Webhook"
        description={
          webhookToDelete ? (
            <div className="space-y-2">
              <p>Are you sure you want to delete this webhook?</p>
              <div className="rounded-md bg-doubleNickel-gray-50 p-3">
                <p className="break-all font-mono text-xs text-doubleNickel-gray-600">
                  {webhookToDelete.url}
                </p>
              </div>
            </div>
          ) : (
            'This action cannot be undone.'
          )
        }
        opened={deleteModalOpened}
        close={closeDeleteModal}
        onDelete={confirmDeleteWebhook}
      />
    </div>
  )
}

export default WebhooksTab
